// src/components/MobileUploadQRModal.tsx
// Shared "scan to upload from phone" QR flow — used by the Patient Records
// Documents tab, the Pre-Consult stage's prior-history upload, the Pharmacy
// walk-in counter's prescription capture, and the self-service kiosk's
// Documents step (bound to an intake, not a patient), so all four offer the
// same scan-and-upload path instead of file-picker only.
//
// Deliberately still a hand-rolled overlay rather than components/ui/Dialog:
// this modal is opened from inside VisitStepper.jsx's sticky stage header
// (zIndex 100) and must sit above it, which is the same reason Dialog itself
// uses z-[200] — see Dialog.tsx's own comment. Moving this onto Radix would
// also change focus-trap and Escape behaviour for two legacy .jsx callers
// that were never built against it. The styling is the design system's; the
// mechanics are unchanged.
import { useState, useEffect, useRef } from 'react';
import { QRCodeSVG } from 'qrcode.react';
import { Camera, TriangleAlert, CircleCheck, Clock } from 'lucide-react';
import { Button } from '@/components/ui/Button';
import { createMobileUploadSession, getMobileSessionStatus, createIntakeUploadSession, getIntakeUploadSessionStatus } from '../api';

// Mirrors the backend's MOBILE_SESSION_TTL_MS (documents.js) — only used
// here for the initial countdown display before the first status poll
// confirms the server's actual expiresAt.
const MOBILE_SESSION_TTL_MS = 15 * 60 * 1000;

interface MobileUploadSession {
  token: string;
  expiresAt: string;
  uploadUrl?: string;
}

/** One document that landed during this session. */
export interface MobileUploadedDoc {
  id: number;
  original_name: string;
  doc_category: string;
  file_type: string | null;
  uploaded_at: string;
}

export interface MobileSessionStatus {
  uploadCount: number;
  expired: boolean;
  /** Newest first. Added so a caller can LINK what arrived, not just refresh a list. */
  documents: MobileUploadedDoc[];
}

interface MobileUploadQRModalProps {
  /** The patient the uploads belong to. Pass this OR intakeId. */
  uhid?: string;
  /**
   * An intake with no patient yet (the self-service kiosk) — uploads wait on
   * the intake until reception registers it (backend utils/intakeDocuments.js).
   */
  intakeId?: number;
  category: string;
  onClose: () => void;
  /**
   * Fired each time the upload count rises. The status payload is optional to
   * consume — the Documents tab and Pre-Consult just refetch their own lists
   * and ignore it; the pharmacy counter reads `documents` to attach the
   * scanned slip to the sale it is building.
   */
  onReceived?: (status: MobileSessionStatus) => void | Promise<void>;
  /** Overrides the default copy, which is written for a patient-facing desk. */
  title?: string;
  hint?: string;
}

export default function MobileUploadQRModal({
  uhid, intakeId, category, onClose, onReceived, title, hint,
}: MobileUploadQRModalProps) {
  const [session, setSession] = useState<MobileUploadSession | null>(null);
  const [error, setError]     = useState('');
  const [receivedCount, setReceivedCount] = useState(0);
  const [secondsLeft, setSecondsLeft]     = useState(0);
  const [expired, setExpired] = useState(false);
  // React 18 StrictMode double-invokes effects in dev (mount→cleanup→mount on
  // the same instance, refs intact) — without this guard, one modal open
  // would mint two sessions/tokens server-side for a single QR shown.
  const createdRef = useRef(false);

  useEffect(() => {
    if (createdRef.current) return;
    createdRef.current = true;
    (intakeId ? createIntakeUploadSession(intakeId, category) : createMobileUploadSession(uhid ?? '', category))
      .then((s: MobileUploadSession) => { setSession(s); setSecondsLeft(Math.round(MOBILE_SESSION_TTL_MS / 1000)); })
      .catch((e: unknown) => setError(e instanceof Error ? e.message : String(e)));
  }, [uhid, intakeId, category]);

  useEffect(() => {
    if (!session) return;
    const id = setInterval(async () => {
      const left = Math.max(0, Math.round((new Date(session.expiresAt).getTime() - Date.now()) / 1000));
      setSecondsLeft(left);
      if (left <= 0) { setExpired(true); return; }
      try {
        const status = (intakeId
          ? await getIntakeUploadSessionStatus(intakeId, session.token)
          : await getMobileSessionStatus(uhid ?? '', session.token)) as MobileSessionStatus;
        if (status.expired) { setExpired(true); return; }
        if (status.uploadCount > receivedCount) {
          setReceivedCount(status.uploadCount);
          await onReceived?.({ ...status, documents: status.documents ?? [] });
        }
      } catch { /* transient poll failure — try again next tick */ }
    }, 3000);
    return () => clearInterval(id);
  }, [session, receivedCount, uhid, intakeId, onReceived]);

  // Prefer the LAN address the backend detected (session.uploadUrl) over
  // window.location.origin — the staff PC's browser is very often pointed at
  // "localhost", which on a phone means the phone itself, not this server.
  const mobileUrl = session ? (session.uploadUrl || `${window.location.origin}?mobile_upload=${session.token}`) : '';
  const usingLocalhost = /^https?:\/\/(localhost|127\.0\.0\.1)([:/]|$)/i.test(mobileUrl);
  const mm = String(Math.floor(secondsLeft / 60)).padStart(2, '0');
  const ss = String(secondsLeft % 60).padStart(2, '0');
  // Under two minutes the countdown stops being background information and
  // becomes something to act on.
  const runningOut = !expired && secondsLeft > 0 && secondsLeft < 120;

  return (
    <div
      className="fixed inset-0 z-[200] flex items-center justify-center bg-black/60 p-4"
      onClick={onClose}
      role="presentation"
    >
      <div
        className="w-full max-w-[360px] rounded-xl border border-border bg-card p-6 text-center shadow-card"
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
        aria-label={title || 'Scan to upload from phone'}
      >
        <div className="mb-1 flex items-center justify-center gap-2 text-[15px] font-bold">
          <Camera size={17} /> {title || 'Scan to upload from phone'}
        </div>
        <div className="mb-4 text-[12px] text-ink-muted">{category}</div>

        {error && (
          <div className="mb-3 rounded-lg bg-danger-soft px-3 py-2 text-[12px] text-danger-ink">{error}</div>
        )}

        {!error && !session && (
          <div className="py-10 text-[13px] text-ink-muted">Generating QR code…</div>
        )}

        {!error && session && !expired && (
          <>
            <div className="mb-3.5 inline-block rounded-xl border border-border bg-white p-3">
              {/* Always on white, never the themed surface — a QR code on a
                  tinted background is a scan failure on some phone cameras. */}
              <QRCodeSVG value={mobileUrl} size={196} />
            </div>

            <p className="mb-2 text-[12px] leading-relaxed text-ink-muted">
              {hint || "Ask the patient (or their attendant) to scan this with their phone's camera to upload photos or documents directly."}
            </p>
            <div className="mb-2 break-all text-[10.5px] text-ink-muted/70">{mobileUrl}</div>

            {usingLocalhost && (
              <div className="mb-2.5 flex gap-2 rounded-lg bg-warn-soft px-3 py-2 text-left text-[11px] text-caution-ink">
                <TriangleAlert size={14} className="mt-px shrink-0" />
                <p className="leading-relaxed">
                  This link points at <b>localhost</b> — a phone can't reach that. This machine's
                  Wi-Fi network couldn't be detected automatically; make sure it's on the same
                  Wi-Fi as the phone, and that the firewall allows inbound connections on this port.
                </p>
              </div>
            )}

            <div
              className={
                'mb-3.5 inline-flex items-center gap-1.5 text-[12px] tabular-nums ' +
                (runningOut ? 'font-semibold text-caution-ink' : 'text-ink-muted')
              }
            >
              <Clock size={12} /> Expires in {mm}:{ss}
            </div>

            {receivedCount > 0 && (
              <div className="mb-3.5 flex items-center justify-center gap-2 rounded-lg bg-success-soft px-3 py-2 text-[12px] text-success-ink">
                <CircleCheck size={14} className="shrink-0" />
                {receivedCount} document{receivedCount > 1 ? 's' : ''} received
              </div>
            )}
          </>
        )}

        {!error && expired && (
          <div className="mb-3.5 rounded-lg bg-warn-soft px-3 py-3.5 text-[13px] text-caution-ink">
            This QR code has expired.
            {receivedCount > 0
              ? ` ${receivedCount} document${receivedCount > 1 ? 's were' : ' was'} received before it expired.`
              : ' Close and generate a new one.'}
          </div>
        )}

        <Button variant="ghost" full onClick={onClose}>
          {receivedCount > 0 ? 'Done' : 'Close'}
        </Button>
      </div>
    </div>
  );
}
